<template>
    <div class="xls">
        <!-- ── intro ─────────────────────────────────────────────── -->
        <header class="xls__hero">
            <span class="xls__hero-icon" aria-hidden="true">
                <v-icon>mdi-microsoft-excel</v-icon>
            </span>
            <div class="xls__hero-text">
                <h2 class="xls__hero-title">{{ $t('YouCanContinueWorking') }}</h2>
                <p class="xls__hero-desc">
                    {{ tr('ExcelRoundTripHint', 'صدّر الإجابات كملف إكسل، عدّلها، ثم أعد رفع الملف لتحديث البيانات.', 'Export the answers to Excel, edit them, then upload the file back to update the data.') }}
                </p>
            </div>
        </header>

        <!-- ── step 1: download ──────────────────────────────────── -->
        <section class="xls-card">
            <span class="xls-card__step" aria-hidden="true">1</span>
            <div class="xls-card__text">
                <h3 class="xls-card__title">{{ tr('DownloadExcelTemplate', 'تنزيل ملف الإكسل', 'Download the Excel file') }}</h3>
                <p class="xls-card__hint">
                    {{ tr('DownloadExcelTemplateHint', 'نزّل الإجابات الحالية كملف إكسل جاهز للتعديل.', 'Download the current answers as a ready-to-edit Excel workbook.') }}
                </p>
            </div>
            <v-btn depressed
                   class="xls-btn xls-btn--excel"
                   :loading="isExporting"
                   :disabled="loadingExport || !value"
                   @click="exportExcel">
                <v-icon small class="me-2">mdi-tray-arrow-down</v-icon>
                {{ $t('EXPORT') }}
            </v-btn>
        </section>

        <!-- ── step 2: upload ────────────────────────────────────── -->
        <section class="xls-card xls-card--upload">
            <div class="xls-card__header">
                <span class="xls-card__step" aria-hidden="true">2</span>
                <div class="xls-card__text">
                    <h3 class="xls-card__title">{{ tr('UploadEditedFile', 'رفع الملف بعد التعديل', 'Upload the edited file') }}</h3>
                    <p class="xls-card__hint">
                        {{ tr('UploadEditedFileHint', 'اسحب الملف وأفلته هنا أو اختره من جهازك.', 'Drag the file here, or pick it from your device.') }}
                    </p>
                </div>
            </div>

            <v-fade-transition mode="out-in">
                <!-- drop zone -->
                <div v-if="!file"
                     key="dropzone"
                     class="xls-drop"
                     :class="{ 'is-over': isDragging, 'is-invalid': !!fileError, 'is-disabled': loadingExport }"
                     role="button"
                     tabindex="0"
                     :aria-label="tr('UploadEditedFile', 'رفع الملف بعد التعديل', 'Upload the edited file')"
                     :aria-disabled="loadingExport ? 'true' : 'false'"
                     @click="browse"
                     @keydown.enter.prevent="browse"
                     @keydown.space.prevent="browse"
                     @dragenter.prevent="onDragEnter"
                     @dragover.prevent="onDragOver"
                     @dragleave.prevent="onDragLeave"
                     @drop.prevent="onDrop">
                    <span class="xls-drop__icon" aria-hidden="true">
                        <v-icon large>{{ isDragging ? 'mdi-tray-arrow-up' : 'mdi-cloud-upload-outline' }}</v-icon>
                    </span>
                    <p class="xls-drop__title">
                        {{ isDragging
                            ? tr('DropFileHere', 'أفلت الملف هنا', 'Drop the file here')
                            : tr('DragAndDropFile', 'اسحب الملف وأفلته هنا', 'Drag and drop your file here') }}
                    </p>
                    <p class="xls-drop__or">
                        {{ tr('Or', 'أو', 'or') }}
                        <span class="xls-drop__browse">{{ tr('BrowseFiles', 'تصفّح الملفات', 'browse files') }}</span>
                    </p>
                    <p class="xls-drop__meta">{{ acceptedLabel }}</p>
                </div>

                <!-- selected file -->
                <div v-else key="file" class="xls-file" :class="fileStateClass">
                    <span class="xls-file__icon" aria-hidden="true">
                        <v-icon>mdi-file-excel-outline</v-icon>
                    </span>
                    <div class="xls-file__text">
                        <p class="xls-file__name" :title="file.name">{{ file.name }}</p>
                        <p class="xls-file__meta">
                            <span>{{ formattedSize }}</span>
                            <span v-if="isImporting" class="xls-file__state">
                                {{ tr('Uploading', 'جارٍ الرفع…', 'Uploading…') }}
                            </span>
                            <span v-else-if="importState === 'success'" class="xls-file__state xls-file__state--ok">
                                <v-icon x-small class="me-1">mdi-check-circle</v-icon>
                                {{ $t('UpdateSuccess') }}
                            </span>
                            <span v-else-if="importState === 'error'" class="xls-file__state xls-file__state--error">
                                <v-icon x-small class="me-1">mdi-alert-circle</v-icon>
                                {{ $t('ErrorOccurred') }}
                            </span>
                            <span v-else-if="importState === 'empty'" class="xls-file__state xls-file__state--warn">
                                <v-icon x-small class="me-1">mdi-information-outline</v-icon>
                                {{ tr('NoAnswersToImport', 'لا توجد إجابات لاستيرادها', 'No answers to import') }}
                            </span>
                            <span v-else-if="isOversized" class="xls-file__state xls-file__state--warn">
                                <v-icon x-small class="me-1">mdi-alert-outline</v-icon>
                                {{ tr('FileLargerThanRecommended', 'حجم الملف أكبر من الحجم الموصى به', 'Larger than the recommended size') }}
                            </span>
                        </p>
                        <v-progress-linear v-if="isImporting"
                                           class="xls-file__progress"
                                           indeterminate
                                           rounded
                                           height="4"
                                           color="primary" />
                    </div>

                    <div class="xls-file__actions">
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn icon
                                       small
                                       class="xls-icon-btn"
                                       :disabled="loadingExport"
                                       :aria-label="tr('ReplaceFile', 'استبدال الملف', 'Replace file')"
                                       v-bind="attrs"
                                       v-on="on"
                                       @click="browse">
                                    <v-icon small>mdi-autorenew</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ tr('ReplaceFile', 'استبدال الملف', 'Replace file') }}</span>
                        </v-tooltip>

                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn icon
                                       small
                                       class="xls-icon-btn xls-icon-btn--danger ms-1"
                                       :disabled="loadingExport"
                                       :aria-label="$t('Remove')"
                                       v-bind="attrs"
                                       v-on="on"
                                       @click="removeFile">
                                    <v-icon small>mdi-close</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ $t('Remove') }}</span>
                        </v-tooltip>
                    </div>
                </div>
            </v-fade-transition>

            <!-- one hidden native input backs both browse and drop -->
            <input ref="fileInput"
                   type="file"
                   class="xls-native-input"
                   accept=".xlsx, .xls"
                   tabindex="-1"
                   aria-hidden="true"
                   @change="onPick">

            <v-slide-y-transition>
                <p v-if="fileError" class="xls-error" role="alert">
                    <v-icon small class="me-1">mdi-alert-circle-outline</v-icon>
                    {{ fileError }}
                </p>
            </v-slide-y-transition>
        </section>

        <!-- ── instructions ──────────────────────────────────────── -->
        <aside class="xls-info">
            <span class="xls-info__icon" aria-hidden="true">
                <v-icon small>mdi-information-outline</v-icon>
            </span>
            <div class="xls-info__text">
                <h4 class="xls-info__title">{{ tr('BeforeYouImport', 'قبل الاستيراد', 'Before you import') }}</h4>
                <ul class="xls-info__list">
                    <li>{{ tr('ExcelHintKeepColumns', 'لا تقم بتغيير أو حذف أعمدة الملف أو ترتيبها.', 'Do not rename, reorder or delete the columns of the file.') }}</li>
                    <li>{{ tr('ExcelHintEditAnswers', 'عدّل الإجابات فقط، مع الإبقاء على المعرّفات كما هي.', 'Edit the answers only and keep the identifier values untouched.') }}</li>
                    <li>{{ tr('ExcelHintOverwrite', 'سيتم تحديث الإجابات الحالية بالبيانات الموجودة في الملف.', 'Existing answers will be updated with the data in the file.') }}</li>
                </ul>
            </div>
        </aside>

        <!-- ── actions ───────────────────────────────────────────── -->
        <footer class="xls-actions">
            <v-btn depressed
                   class="xls-btn xls-btn--muted"
                   :disabled="loadingExport"
                   @click="$emit('close')">
                {{ $t('Cancel') }}
            </v-btn>
            <v-btn depressed
                   color="secondary"
                   class="xls-btn xls-btn--primary white--text ms-3"
                   :loading="isImporting"
                   :disabled="!file || !value || loadingExport"
                   @click="importExcel">
                <v-icon small class="me-2">mdi-database-import-outline</v-icon>
                {{ tr('ImportExcel', 'استيراد الملف', 'Import Excel') }}
            </v-btn>
        </footer>
    </div>
</template>

<script>
    import SurveyService from '@/services/SurveyService'
    import { downloadFile } from '@/helpers/downloader.js'
    export default {
        props: {
            value: {
                type: Number,
                default: 0
            },

            // size shown in the upload hint. Display only - nothing is blocked by it,
            // the server remains the single source of truth for what it accepts.
            maxSizeMb: {
                type: Number,
                default: 10
            }
        },

        data() {
            return {
                file: null, // File selected for import
                loadingExport: false,
                // presentation state only - which button owns the spinner, and how the
                // selected file card should read after an import attempt
                activeAction: null,
                importState: 'idle',
                isDragging: false,
                dragDepth: 0,
                fileError: null
            }
        },

        computed: {
            isExporting() {
                return this.loadingExport && this.activeAction === 'export'
            },

            isImporting() {
                return this.loadingExport && this.activeAction === 'import'
            },

            acceptedExtensions() {
                return ['.xlsx', '.xls']
            },

            acceptedLabel() {
                return `${this.acceptedExtensions.join(', ')} · ${this.tr('MaxSize', 'حتى', 'up to')} ${this.maxSizeMb} MB`
            },

            formattedSize() {
                if (!this.file) {
                    return ''
                }
                const kb = this.file.size / 1024
                return kb < 1024 ? `${kb.toFixed(0)} KB` : `${(kb / 1024).toFixed(2)} MB`
            },

            isOversized() {
                return !!this.file && this.file.size > this.maxSizeMb * 1024 * 1024
            },

            fileStateClass() {
                return {
                    'is-uploading': this.isImporting,
                    'is-success': this.importState === 'success',
                    'is-error': this.importState === 'error'
                }
            }
        },

        methods: {

            // $t() returns the key itself when a translation is missing, so the codebase's
            // `$t('X') || 'fallback'` idiom never fires. $te() actually tests for existence.
            tr(key, ar, en) {
                if (this.$te(key)) return this.$t(key)
                return this.$i18n && this.$i18n.locale === 'en' ? en : ar
            },

            browse() {
                if (this.loadingExport) {
                    return
                }
                this.$refs.fileInput.click()
            },

            onPick(event) {
                const picked = event.target.files && event.target.files[0]
                if (picked) {
                    this.setFile(picked)
                }
                // lets the same file be picked again after it was removed
                event.target.value = ''
            },

            onDragEnter() {
                if (this.loadingExport) {
                    return
                }
                this.dragDepth++
                this.isDragging = true
            },

            onDragOver() {
                if (!this.loadingExport) {
                    this.isDragging = true
                }
            },

            onDragLeave() {
                this.dragDepth--
                if (this.dragDepth <= 0) {
                    this.dragDepth = 0
                    this.isDragging = false
                }
            },

            onDrop(event) {
                this.dragDepth = 0
                this.isDragging = false
                if (this.loadingExport) {
                    return
                }
                const dropped = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]
                if (dropped) {
                    this.setFile(dropped)
                }
            },

            // mirrors the accept=".xlsx, .xls" attribute, which the browse dialog applies
            // on its own but a drop does not
            setFile(candidate) {
                const name = (candidate.name || '').toLowerCase()
                const allowed = this.acceptedExtensions.some(ext => name.endsWith(ext))

                if (!allowed) {
                    this.fileError = this.tr(
                        'UnsupportedFileType',
                        `نوع الملف غير مدعوم. الأنواع المسموحة: ${this.acceptedExtensions.join('، ')}`,
                        `Unsupported file type. Allowed: ${this.acceptedExtensions.join(', ')}`)
                    return
                }

                this.fileError = null
                this.importState = 'idle'
                this.file = candidate
            },

            removeFile() {
                this.file = null
                this.fileError = null
                this.importState = 'idle'
                if (this.$refs.fileInput) {
                    this.$refs.fileInput.value = ''
                }
            },

            // Export Excel
            async exportExcel() {
                this.loadingExport = true
                this.activeAction = 'export'
                SurveyService.exportToExcelForEdit(this.value).then(res => {
                    downloadFile(res)
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loadingExport = false
                    this.activeAction = null
                })
            },

            // Import Excel
            async importExcel() {
                try {
                    this.loadingExport = true
                    this.activeAction = 'import'
                    this.importState = 'uploading'
                    const formData = new FormData()
                    formData.append('file', this.file)
                    var data = await SurveyService.importExcelForEdit(this.value, formData)
                    if (data.success) {
                        this.importState = 'success'
                        this.$root.$notification(this.$t('UpdateSuccess'), 'success')
                    }
                    else if (data.data && data.data.answersFound === 0) {
                        // the file had no answer rows to update (e.g. a survey with no responses) —
                        // this is not a failure, so show an informational message, not a red error
                        this.importState = 'empty'
                        this.$root.$notification(this.tr('NoAnswersToImport', 'لا توجد إجابات لاستيرادها في الملف', 'There are no answers to import in the file'), 'info')
                    }
                    else {
                        this.importState = 'error'
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }
                }
                catch (ex) {
                    this.importState = 'error'
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }
                this.loadingExport = false
                this.activeAction = null
            },
        },
    }
</script>

<style>
    /* ==========================================================================
       "Edit answers via Excel" dialog body.
       Rendered inside a dialog that is teleported out of the page, so everything
       is namespaced with .xls*. Direction-dependent rules rely on Vuetify's
       .v-application--is-rtl setting `direction: rtl`, so logical properties flip.
       ========================================================================== */

    .xls {
        --xls-surface: #ffffff;
        --xls-border: #e4e9ee;
        --xls-text: #2b3a42;
        --xls-muted: #78899a;
        --xls-radius: 14px;
        /* Muted teal-green accent, matching the e-library and organizational-structure
           pages. It replaces --v-primary-lighten5, which resolved to a cyan. Distinct
           from --xls-excel-tint below, which is Excel's own brand green. */
        --xls-tint: #84a6a5;
        --xls-excel: #1d6f42;
        --xls-excel-tint: #e8f4ed;
        padding: 20px 22px 18px;
        background: #f7f9fa;
        animation: xls-enter .28s ease both;
    }

    @keyframes xls-enter {
        from {
            opacity: 0;
            transform: translateY(8px);
        }

        to {
            opacity: 1;
            transform: none;
        }
    }

    /* ---------- intro ---------- */
    .xls__hero {
        display: flex;
        align-items: center;
        gap: 14px;
        margin-bottom: 18px;
    }

    .xls__hero-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 52px;
        height: 52px;
        flex: 0 0 52px;
        border-radius: 15px;
        background: var(--xls-excel-tint);
        border: 1px solid #cfe6da;
    }

    .xls__hero-icon .v-icon {
        color: var(--xls-excel) !important;
        font-size: 30px;
    }

    .xls__hero-text {
        min-width: 0;
    }

    .xls__hero-title {
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 17px;
        font-weight: 600;
        color: var(--xls-text);
        line-height: 1.35;
        margin: 0 0 2px;
    }

    .xls__hero-desc {
        font-size: 13px;
        color: var(--xls-muted);
        line-height: 1.55;
        margin: 0;
    }

    /* ---------- step cards ---------- */
    .xls-card {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 14px 16px;
        margin-bottom: 14px;
        border: 1px solid var(--xls-border);
        border-radius: var(--xls-radius);
        background: var(--xls-surface);
    }

    .xls-card--upload {
        display: block;
    }

    .xls-card__header {
        display: flex;
        align-items: center;
        gap: 14px;
        margin-bottom: 14px;
    }

    .xls-card__step {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
        flex: 0 0 26px;
        border-radius: 50%;
        background: var(--xls-tint);
        color: var(--v-primary-base);
        font-size: 13px;
        font-weight: 700;
        line-height: 1;
    }

    .xls-card__text {
        flex: 1 1 auto;
        min-width: 0;
    }

    .xls-card__title {
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 14px;
        font-weight: 600;
        color: var(--xls-text);
        line-height: 1.4;
        margin: 0;
    }

    .xls-card__hint {
        font-size: 12.5px;
        color: var(--xls-muted);
        line-height: 1.5;
        margin: 0;
    }

    /* ---------- drop zone ---------- */
    .xls-drop {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 26px 18px;
        border: 1.5px dashed #cbd5dd;
        border-radius: 12px;
        background: #fbfcfd;
        cursor: pointer;
        transition: border-color .18s ease, background .18s ease, box-shadow .18s ease, transform .18s ease;
    }

    .xls-drop:hover {
        border-color: var(--v-primary-base);
        background: var(--xls-tint);
    }

    .xls-drop:focus {
        outline: none;
        border-color: var(--v-primary-base);
        box-shadow: 0 0 0 3px rgba(10, 113, 110, .18);
    }

    .xls-drop.is-over {
        border-color: var(--v-primary-base);
        border-style: solid;
        background: var(--xls-tint);
        transform: scale(1.01);
    }

    .xls-drop.is-invalid {
        border-color: var(--v-error-base);
        background: #fdf1f1;
    }

    .xls-drop.is-disabled {
        cursor: not-allowed;
        opacity: .55;
        pointer-events: none;
    }

    .xls-drop__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 54px;
        height: 54px;
        border-radius: 50%;
        background: #eef2f5;
        margin-bottom: 12px;
        transition: background .18s ease;
    }

    .xls-drop:hover .xls-drop__icon,
    .xls-drop.is-over .xls-drop__icon {
        background: #fff;
    }

    .xls-drop__icon .v-icon {
        color: var(--v-primary-base) !important;
    }

    .xls-drop.is-invalid .xls-drop__icon .v-icon {
        color: var(--v-error-base) !important;
    }

    .xls-drop__title {
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 14px;
        font-weight: 600;
        color: var(--xls-text);
        margin: 0 0 2px;
    }

    .xls-drop__or {
        font-size: 13px;
        color: var(--xls-muted);
        margin: 0 0 10px;
    }

    .xls-drop__browse {
        color: var(--v-primary-base);
        font-weight: 600;
        text-decoration: underline;
    }

    .xls-drop__meta {
        font-size: 12px;
        color: var(--xls-muted);
        margin: 0;
        letter-spacing: .01em;
    }

    .xls-native-input {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }

    /* ---------- selected file ---------- */
    .xls-file {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 14px;
        border: 1px solid #cfe6da;
        border-radius: 12px;
        background: var(--xls-excel-tint);
        animation: xls-pop .22s ease both;
    }

    @keyframes xls-pop {
        from {
            opacity: 0;
            transform: scale(.98);
        }

        to {
            opacity: 1;
            transform: none;
        }
    }

    .xls-file.is-success {
        border-color: #b6dfc9;
        background: #e7f4ee;
    }

    .xls-file.is-error {
        border-color: #f0c4c4;
        background: #fdf1f1;
    }

    .xls-file__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 42px;
        height: 42px;
        flex: 0 0 42px;
        border-radius: 12px;
        background: #fff;
        border: 1px solid #d8e8df;
    }

    .xls-file__icon .v-icon {
        color: var(--xls-excel) !important;
    }

    .xls-file.is-error .xls-file__icon .v-icon {
        color: var(--v-error-base) !important;
    }

    .xls-file__text {
        flex: 1 1 auto;
        min-width: 0;
    }

    .xls-file__name {
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 14px;
        font-weight: 600;
        color: var(--xls-text);
        line-height: 1.4;
        margin: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .xls-file__meta {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 4px 10px;
        font-size: 12px;
        color: var(--xls-muted);
        line-height: 1.5;
        margin: 0;
    }

    .xls-file__state {
        display: inline-flex;
        align-items: center;
        font-weight: 600;
    }

    .xls-file__state .v-icon {
        color: inherit !important;
    }

    .xls-file__state--ok {
        color: #1b8354;
    }

    .xls-file__state--error {
        color: var(--v-error-base);
    }

    .xls-file__state--warn {
        color: #b26a00;
    }

    .xls-file__progress {
        margin-top: 8px;
    }

    .xls-file__actions {
        display: flex;
        align-items: center;
        flex: 0 0 auto;
    }

    /* ---------- error line ---------- */
    .xls-error {
        display: flex;
        align-items: center;
        margin: 10px 0 0;
        font-size: 12.5px;
        color: var(--v-error-base);
        line-height: 1.5;
    }

    .xls-error .v-icon {
        color: var(--v-error-base) !important;
    }

    /* ---------- instructions ---------- */
    .xls-info {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 14px 16px;
        border: 1px solid #cfe0ee;
        border-radius: var(--xls-radius);
        background: #f1f7fc;
    }

    .xls-info__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        flex: 0 0 28px;
        border-radius: 8px;
        background: #ffffff;
        border: 1px solid #d6e6f3;
        margin-top: 1px;
    }

    .xls-info__icon .v-icon {
        color: #2b7bb9 !important;
    }

    .xls-info__title {
        font-family: 'IBMPlexSansArabic-SemiBold', 'TheSansArabic', sans-serif;
        font-size: 13px;
        font-weight: 600;
        color: #1f4f74;
        margin: 0 0 6px;
        line-height: 1.4;
    }

    .xls-info__list {
        margin: 0;
        padding-inline-start: 18px;
        font-size: 12.5px;
        color: #3c6889;
        line-height: 1.7;
    }

    .xls-info__list li::marker {
        color: #8fb6d3;
    }

    /* ---------- actions ---------- */
    .xls-actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        margin-top: 18px;
        padding-top: 16px;
        border-top: 1px solid var(--xls-border);
    }

    .xls-btn {
        border-radius: 10px;
        text-transform: none;
        letter-spacing: 0;
        font-size: 14px !important;
        font-weight: 600;
        padding: 0 16px;
    }

    .xls-btn--muted {
        background: #eef1f4 !important;
        color: var(--xls-text) !important;
    }

    .xls-btn--primary {
        min-width: 150px !important;
        box-shadow: 0 4px 12px rgba(0, 154, 123, .22) !important;
    }

    .xls-btn--primary.v-btn--disabled {
        box-shadow: none !important;
    }

    .xls-btn--excel {
        background: var(--xls-excel) !important;
        color: #fff !important;
    }

    .xls-btn--excel.v-btn--disabled {
        background: #e3e7ea !important;
        color: var(--xls-muted) !important;
    }

    .xls-btn--excel .v-icon {
        color: #fff !important;
    }

    /* icon-only buttons fill with colour on hover/focus, because the global
       stylesheet forces icons inside a hovered/focused button to white */
    .xls-icon-btn {
        border-radius: 9px;
        background: rgba(255, 255, 255, .75);
        transition: background .18s ease;
    }

    .xls-icon-btn .v-icon {
        color: var(--xls-muted);
    }

    .xls-icon-btn:hover,
    .xls-icon-btn:focus {
        background: var(--v-primary-base);
    }

    .xls-icon-btn--danger:hover,
    .xls-icon-btn--danger:focus {
        background: var(--v-error-base);
    }

    /* ---------- responsive ---------- */
    @media (max-width: 599px) {
        .xls {
            padding: 16px 14px 14px;
        }

        .xls__hero {
            gap: 12px;
            margin-bottom: 14px;
        }

        .xls__hero-icon {
            width: 44px;
            height: 44px;
            flex: 0 0 44px;
        }

        .xls__hero-icon .v-icon {
            font-size: 24px;
        }

        .xls-card {
            flex-wrap: wrap;
        }

        .xls-card .xls-btn--excel {
            width: 100%;
        }

        .xls-drop {
            padding: 20px 14px;
        }

        .xls-actions {
            flex-wrap: wrap;
            gap: 10px;
        }

        .xls-actions .xls-btn {
            flex: 1 1 100%;
            margin: 0 !important;
        }

        .xls-actions .xls-btn--primary {
            order: -1;
        }
    }
</style>
